import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ConvertInquiry from "@/components/ConvertInquiry";

/** Landing for the website admin's "Convert to Project" button:
 *  /inquiries/:inquiryId/convert?ticket=… — the ticket is redeemed before anything renders. */
export default function ConvertFromWebsite() {
  const { inquiryId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [inquiry, setInquiry] = useState(null);
  const [error, setError] = useState("");
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const loadInquiry = async () => {
      try {
        const { data } = await api.get(`/inquiries/${inquiryId}`);
        if (data.local_project) {
          navigate(`/projects/${data.local_project.id}`, { replace: true });
          return;
        }
        if (data.converted_project_id) {
          navigate(`/projects/${data.converted_project_id}`, { replace: true });
          return;
        }
        setInquiry(data);
      } catch (e) {
        setError(apiError(e));
      }
    };

    const run = async () => {
      const ticket = params.get("ticket");
      if (ticket) {
        const used = sessionStorage.getItem("asthetik_sso_ticket") === ticket;
        window.history.replaceState({}, "", `/inquiries/${inquiryId}/convert`);
        if (!used) {
          sessionStorage.setItem("asthetik_sso_ticket", ticket);
          try {
            const { data } = await api.post("/sso/redeem", { ticket });
            if (data.access_token) localStorage.setItem("asthetik_token", data.access_token);
            await refresh();
          } catch (e) {
            setError(apiError(e));
            return;
          }
        }
      }
      await loadInquiry();
    };

    run();
  }, [inquiryId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center px-6" data-testid="convert-landing-error">
        <div className="w-full max-w-md text-center">
          <div className="font-serif text-2xl tracking-tight text-ink">Ästhetik<span className="text-lime">.</span></div>
          <p className="mt-8 rounded-sm border border-[#E07A5F] bg-[#F6DED6] px-5 py-4 text-sm leading-relaxed text-[#A8492E]">
            {error}
          </p>
          <button data-testid="convert-landing-login" onClick={() => navigate("/login", { replace: true })}
            className="mt-6 text-xs uppercase tracking-[0.15em] text-stone underline-offset-4 hover:underline">
            Sign in with a password instead
          </button>
        </div>
      </div>
    );
  }

  if (!inquiry) {
    return (
      <div className="flex min-h-screen items-center justify-center px-6" data-testid="convert-landing-loading">
        <p className="flex items-center gap-3 text-sm text-stone">
          <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
          Opening this inquiry…
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone" data-testid="convert-landing">
      <ConvertInquiry
        inquiry={inquiry}
        onClose={() => navigate("/inquiries", { replace: true })}
        onDone={() => {}}
      />
    </div>
  );
}
